<template>
  <div id="validationDrawerId">
    <el-drawer :visible="drawerDisplay" :with-header="withHeader" :close-on-click-modal="closeOnClickModal"
      :close-on-press-escape="closeOnPressEscape" :show-close="false" @close="closeDrawer" :size="width"
      :direction="direction">
      <div class="drawer-container">
        <div class="drawer-header">
          <span class="drawer-title">{{ titleText }}</span>
          <span @click="closeDrawer" class="icon-close-container">
            <img class="icon-close" src="@/assets/image/common/icon-close.svg" alt="">
          </span>
        </div>

        <div class="drawer-body" v-if="drawerDisplay">
          <slot name="prefix"></slot>
          <validation-component ref="validationForm" @afterViewInit="onAfterFormInit"
            :validation-condition="validationCondition">
          </validation-component>
          <slot></slot>
        </div>

        <div class="drawer-footer">
          <div v-if="submitErrorMessage" class="error-box">{{ submitErrorMessage }}</div>
          <span class="drawer-footer-btns">
            <el-button v-if="showCancelButton" @click="handleCancel">{{ $t('message.tableCommon.cancel') }}</el-button>
            <el-button v-if="isShowButton" type="primary" :disabled="submitDisabled" @click="handleDetermine"
              :loading="isButtonLoading">{{ submitButtonText || $t('message.common.submit') }}</el-button>
          </span>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ShowSuccessMessage } from '@/components/modal/tips/message-tips.js';
import ValidationComponent from '@/components/validation/validation.vue';
import { shallowClones } from '@/utils/tools/utils.js';

export default {
  name: 'ValidateDrawer',
  components: {
    ValidationComponent,
  },
  props: {
    titleText: {
      type: String,
      default: '',
    },
    width: {
      type: [String, Number],
      default: 580,
    },
    direction: {
      type: String,
      default: 'rtl',
    },
    validationCondition: {
      type: Array,
      default: () => [],
    },
    defaultFunction: {
      type: Function,
      default: null,
    },
    adjustParameters: {
      type: Function,
      default: null,
    },
    beforeSubmitJudgement: {
      type: Function,
      default: null,
    },
    closeOnClickModal: {
      type: Boolean,
      default: false,
    },
    closeOnPressEscape: {
      type: Boolean,
      default: true,
    },
    submitSuccessTip: {
      type: String,
      default: 'message.submitSuccessfulTip',
    },
    isShowButton: {
      type: Boolean,
      default: true,
    },
    submitButtonText: {
      type: String,
      default: '',
    },
    submitErrorMessage: {
      type: String,
      default: '',
    },
    showSubmitMessage: {
      type: Boolean,
      default: true,
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    customCloseFn: {
      type: Function,
      default: null,
    },
    customCloseState: {
      type: Boolean,
      default: true,
    },
    showCancelButton: {
      type: Boolean,
      default: false,
    },
    withHeader: {
      type: Boolean,
      default: true,
    },
    withFooter: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      drawerDisplay: false,
      isButtonLoading: false,
      modifyFunction: null,
      modifyData: null,
      operating: false,
    };
  },
  methods: {
    getForm() {
      return this.$refs.validationForm;
    },
    openDrawer(isFormReset = true) {
      this.drawerDisplay = true;
      if (isFormReset && this.getForm()) {
        this.getForm().formReset();
      }
    },
    openDrawerAndCallBack(callBack, isFormReset = true) {
      this.openDrawer(isFormReset);
      if (callBack) {
        const count = setTimeout(() => {
          callBack(this.getForm());
          clearTimeout(count);
        });
      }
    },
    closeDrawer() {
      if (!this.customCloseState) {
        const submitFunction = this.customCloseFn;
        submitFunction();
      } else {
        this.drawerDisplay = false;
        this.modifyFunction = null;
        this.modifyData = null;
        this.isButtonLoading = false;
      }
    },
    setModifyCondition(data, modifyFunc) {
      this.modifyData = data;
      this.modifyFunction = modifyFunc;
    },
    httpSubmit() {
      if (this.defaultFunction || this.modifyFunction) {
        let params = this.modifyData ? shallowClones(this.getForm().getFormJson(), this.modifyData) : this.getForm().getFormJson();
        if (!this.beforeSubmitJudgement || (this.beforeSubmitJudgement && this.beforeSubmitJudgement(params))) {
          this.isButtonLoading = true;
          params = this.adjustParameters ? this.adjustParameters(params) : params;
          const submitFunction = this.modifyFunction || this.defaultFunction;
          submitFunction(params).then((data) => {
            this.closeDrawer();
            this.showSubmitMessage && ShowSuccessMessage(this.$t(this.submitSuccessTip));
            this.$emit('refresh', data);
            this.isButtonLoading = false;
          }).catch(() => {
            this.isButtonLoading = false;
          });
        }
      }
    },
    handleDetermine() {
      if (this.operating) {
        return;
      }
      this.operating = true;
      setTimeout(() => {
        this.operating = false;
      }, 200);
      this.getForm().toFormValid(this.httpSubmit);
    },
    onAfterFormInit() {
      this.$emit('afterDrawerFormInit');
    },
    handleCancel() {
      this.drawerDisplay = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

#validationDrawerId {
  .drawer-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 24px;
  }

  .drawer-header {
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .drawer-title {
      font-family: " Open Sans-regular";
      font-weight: bold;
      font-size: 20px;
      color: #1A202C;
      line-height: 30px;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }

    .icon-close-container {
      width: 18px;
      height: 18px;
      padding: 3px;

      .icon-close {
        width: 15px;
        height: 15px;
      }

      &:hover {
        cursor: pointer;
        background: $color-default;
      }
    }
  }

  .drawer-body {
    flex: 1;
    padding-top: 24px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .drawer-footer {
    border-top: 1px solid #EFEFF4;
    padding-top: 12px;

    .error-box {
      text-align: left;
      color: $color-danger;
      line-height: 1.2;
      margin-bottom: 15px;
    }

    .drawer-footer-btns {
      display: flex;
      justify-content: center;
      gap: 12px;

      .el-button {
        flex: 1;
      }
    }
  }
}
</style>
